import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { orgApi } from "../utils/apiV2";
import api from "../utils/api";  // Keep for file uploads (not migrated yet)
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import ServiceToggle from "./ServiceToggle";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { 
  Plus, Trash2, Package, Edit, ArrowUpDown, ArrowUp, ArrowDown, 
  CheckSquare, Users, X, Upload, FileText, Image as ImageIcon, Shield, 
  Loader2, Settings, AlertCircle, Check, Eye, LayoutGrid, List
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import Pagination from "./Pagination";
import AddAssetModal from "./AddAssetModal";
import TabPageLayout from './TabPageLayout';
import CardListViewToggle from './CardListViewToggle';
import MassActionToolbar from './MassActionToolbar';
import EmptyState from './EmptyState';
import { PrimaryButton, SecondaryButton, DangerButton } from './Buttons';
import StandardModal from './StandardModal';
import InfoCard from './InfoCard';
import { useRoles } from '../hooks/useData';
import RoleSelector from './RoleSelector';
import ResourceCard from './ResourceCard';
import ImageViewer from './ImageViewer';

const AssetsTab = ({ assets, teamMembers, onRefresh, isLoading: propsLoading, isError: propsError }) => {
  const navigate = useNavigate();
  const { roles } = useRoles();
  const [open, setOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewMode, setViewMode] = useState("card");
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: "" });
  const [editingAsset, setEditingAsset] = useState(null);

  const getAssetBorderColors = (asset) => {
    if (!asset.teamRequirements || asset.teamRequirements.length === 0) return [];
    const roleColors = asset.teamRequirements
      .map(req => {
        // Support both new roleIds[] and legacy roleId
        const primaryId = (req.roleIds && req.roleIds[0]) || req.roleId;
        const role = roles.find(r => r.id === primaryId);
        return role?.color || '#3B82F6';
      })
      .filter((color, index, self) => self.indexOf(color) === index);
    return roleColors;
  };

  // Form states
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState("12");
  const [active, setActive] = useState(true);
  const [notes, setNotes] = useState("");
  const [assetImages, setAssetImages] = useState([]);
  const [assetDocuments, setAssetDocuments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [teamRequirements, setTeamRequirements] = useState([]);
  
  // Image viewer state
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0);
  const [viewerTitle, setViewerTitle] = useState("Asset Photos");
  
  // Sort and pagination states
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  
  // Mass action states
  const [massActionMode, setMassActionMode] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [expandedAssets, setExpandedAssets] = useState({});

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 inline" />;
    return sortDirection === "asc" ? 
      <ArrowUp className="w-3 h-3 ml-1 inline" /> : 
      <ArrowDown className="w-3 h-3 ml-1 inline" />;
  };
  
  const toggleMassActionMode = () => {
    setMassActionMode(!massActionMode);
    setSelectedAssets([]);
  };
  
  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };
  
  const selectAllAssets = () => {
    setSelectedAssets(paginatedAssets.map(asset => asset.id));
  };
  
  const deselectAllAssets = () => {
    setSelectedAssets([]);
  };
  
  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const deletePromises = selectedAssets.map(assetId =>
        orgApi.deleteAsset(assetId)
      );
      
      await Promise.all(deletePromises);
      toast.success(`${selectedAssets.length} asset(s) deleted successfully`);
      setSelectedAssets([]);
      setMassActionMode(false);
      setBulkDeleteConfirm(false);
      onRefresh();
    } catch (error) {
      console.error("Error deleting assets:", error);
      toast.error("Failed to delete some assets. They may be assigned to trips.");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleEditClick = (asset) => {
    setEditingAsset(asset);
    setName(asset.name);
    setCapacity(asset.capacity?.toString() || "12");
    setActive(asset.active !== undefined ? asset.active : true);
    setNotes(asset.notes || "");
    setAssetImages(asset.photos && asset.photos.length > 0 
      ? asset.photos 
      : (asset.photoUrl && asset.photoUrl.startsWith('http')) 
        ? [asset.photoUrl] 
        : []);
    setAssetDocuments(asset.documents || []);
    setTeamRequirements(asset.teamRequirements || []);
    setOpen(true);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (assetImages.length + files.length > 5) {
      toast.error("Maximum 5 photos allowed per asset");
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const uploadedUrls = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/upload/asset-photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedUrls.push(response.data.url);
      }
      
      setAssetImages(prev => [...prev, ...uploadedUrls]);
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (error) {
      console.error("Error uploading photos:", error);
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };
  
  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (assetDocuments.length + files.length > 10) {
      toast.error("Maximum 10 documents allowed per asset");
      return;
    }
    
    setUploadingDocument(true);
    try {
      const uploadedDocs = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/upload/asset-document', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedDocs.push({
          filename: file.name,
          url: response.data.url,
          uploadedAt: new Date().toISOString(),
          size: file.size
        });
      }
      
      setAssetDocuments(prev => [...prev, ...uploadedDocs]);
      toast.success(`${files.length} document(s) uploaded`);
    } catch (error) {
      console.error("Error uploading documents:", error);
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploadingDocument(false);
    }
  };
  
  const handleRemovePhoto = (index) => {
    setAssetImages(prev => prev.filter((_, i) => i !== index));
    toast.success("Photo removed");
  };
  
  const handleRemoveDocument = (index) => {
    setAssetDocuments(prev => prev.filter((_, i) => i !== index));
    toast.success("Document removed");
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Please enter an asset name");
      return;
    }

    const duplicateAsset = assets.find(a => 
      a.name.toLowerCase().trim() === name.toLowerCase().trim() && 
      a.id !== editingAsset?.id
    );
    
    if (duplicateAsset) {
      toast.error(`An asset named "${name}" already exists`);
      return;
    }

    setSaving(true);
    try {
      const payload = { 
        name: name.trim(), 
        capacity: parseInt(capacity) || 12,
        active, 
        notes: notes?.trim() || null,
        photoUrl: assetImages.length > 0 ? assetImages[0] : null,
        photos: assetImages,
        documents: assetDocuments,
        teamRequirements: teamRequirements
      };
      
      if (editingAsset) {
        await orgApi.updateAsset(editingAsset.id, payload);
        toast.success("Asset updated successfully");
      } else {
        await orgApi.createAsset(payload);
        toast.success("Asset added successfully");
      }
      setOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error("Error saving asset:", error);
      toast.error("Failed to save asset");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (asset) => {
    setDeleteConfirm({ open: true, id: asset.id, name: asset.name });
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await orgApi.deleteAsset(deleteConfirm.id);
      toast.success("Asset deleted successfully");
      setDeleteConfirm({ open: false, id: null, name: "" });
      onRefresh();
    } catch (error) {
      console.error("Error deleting asset:", error);
      const errorMsg = error.response?.data?.detail || "Failed to delete asset";
      
      if (error.response?.status === 400) {
        toast.error(errorMsg, { duration: 6000 });
      } else {
        toast.error("Failed to delete asset");
      }
      
      setDeleteConfirm({ open: false, id: null, name: "" });
    } finally {
      setIsDeleting(false);
    }
  };

  const resetForm = () => {
    setEditingAsset(null);
    setName("");
    setCapacity("12");
    setActive(true);
    setNotes("");
    setAssetImages([]);
    setAssetDocuments([]);
    setTeamRequirements([]);
  };

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) {
      resetForm();
    }
  };

  // Sort assets
  const sortedAssets = [...(assets || [])].sort((a, b) => {
    let compareValue = 0;
    
    switch (sortField) {
      case "name":
        compareValue = a.name.localeCompare(b.name);
        break;
      case "type":
        compareValue = (a.type || '').localeCompare(b.type || '');
        break;
      default:
        compareValue = 0;
    }
    
    return sortDirection === "asc" ? compareValue : -compareValue;
  });

  // Pagination
  const totalPages = Math.ceil(sortedAssets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAssets = sortedAssets.slice(startIndex, startIndex + itemsPerPage);

  // Error state
  if (propsError) {
    return (
      <TabPageLayout
        title="Assets"
        subtitle="Register your resources"
      >
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Failed to load assets</h3>
          <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
          <PrimaryButton onClick={() => onRefresh()}>
            Try Again
          </PrimaryButton>
        </div>
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Assets"
      subtitle="Register your resources"
      loading={propsLoading}
      actions={
        <div className="flex items-center gap-2">
          {/* Desktop only: mass action + view toggles */}
          <div className="hidden sm:flex items-center gap-0.5">
            <button
              onClick={toggleMassActionMode}
              className={`h-8 w-8 flex items-center justify-center rounded-lg transition-all ${massActionMode ? 'opacity-100 text-blue-600' : 'opacity-40 text-gray-600 hover:opacity-70'}`}
            >
              <CheckSquare className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("card")}
              className={`h-8 w-8 flex items-center justify-center rounded-lg transition-all ${viewMode === "card" ? 'opacity-100 text-blue-600' : 'opacity-40 text-gray-600 hover:opacity-70'}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`h-8 w-8 flex items-center justify-center rounded-lg transition-all ${viewMode === "list" ? 'opacity-100 text-blue-600' : 'opacity-40 text-gray-600 hover:opacity-70'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          <PrimaryButton onClick={() => setShowAddModal(true)} className="h-8 px-3">
            <Plus className="w-4 h-4 mr-1" />
            Add
          </PrimaryButton>
        </div>
      }
    >
      <div data-testid="assets-tab" className="space-y-6">
        <AddAssetModal 
          isOpen={showAddModal} 
          onClose={() => setShowAddModal(false)} 
          onSuccess={onRefresh}
          teamMembers={teamMembers}
        />
        
        {/* Edit Asset Modal */}
        {editingAsset && (
          <StandardModal
            isOpen={open}
            onClose={handleOpenChange}
            title="Edit Asset"
            description={`Update ${editingAsset.name}'s details`}
            size="md"
            footer={
              <>
                <SecondaryButton onClick={() => handleOpenChange(false)} className="flex-1 sm:flex-none" disabled={saving}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton onClick={handleSubmit} disabled={saving} className="flex-1 sm:flex-none">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </PrimaryButton>
              </>
            }
          >
            <Tabs defaultValue="basic" className="w-full">
              <div className="mb-4">
                <TabsList className="grid w-full grid-cols-4 h-auto sm:h-11">
                  <TabsTrigger value="basic" className="text-xs sm:text-sm py-2">Basic</TabsTrigger>
                  <TabsTrigger value="team" className="text-xs sm:text-sm py-2">Team</TabsTrigger>
                  <TabsTrigger value="photos" className="text-xs sm:text-sm py-2">Photos</TabsTrigger>
                  <TabsTrigger value="documents" className="text-xs sm:text-sm py-2">Docs</TabsTrigger>
                </TabsList>
              </div>
              
              {/* Tab 1: Basic Info */}
              <TabsContent value="basic" className="space-y-4">
                <div>
                  <Label htmlFor="asset-name">Asset Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="asset-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Sea Breeze"
                    className={`mt-1 ${!name.trim() && name !== '' ? 'border-red-300 focus:border-red-500' : ''}`}
                  />
                  {!name.trim() && name !== '' && (
                    <p className="text-sm text-red-500 mt-1">Asset name is required</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="asset-capacity">Capacity <span className="text-red-500">*</span></Label>
                    <Input
                      id="asset-capacity"
                      type="number"
                      value={capacity}
                      onChange={(e) => setCapacity(e.target.value)}
                      placeholder="12"
                      className="mt-1"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-7">
                    <Label>Active {!active && <span className="text-xs text-red-500 font-normal">(won't be assigned to trips)</span>}</Label>
                    <ServiceToggle
                      checked={active}
                      onChange={setActive}
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="asset-notes">Notes (Optional)</Label>
                  <Textarea
                    id="asset-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g., Under maintenance until Dec 15th"
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </TabsContent>
              
              {/* Tab 2: Team Requirements */}
              <TabsContent value="team" className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4" />
                    Team Requirements
                  </Label>
                  <p className="text-sm text-gray-500 mb-4">
                    Define which roles are needed to operate this asset
                  </p>
                  
                  <div className="space-y-2">
                    {teamRequirements.length === 0 ? (
                      <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500 mb-3">No team requirements set</p>
                        <SecondaryButton
                          onClick={() => setTeamRequirements([{ roleIds: [''], quantity: 1 }])}
                          size="sm"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Role Requirement
                        </SecondaryButton>
                      </div>
                    ) : (
                      <>
                        {teamRequirements.map((requirement, index) => {
                          // Normalise legacy { roleId } → { roleIds }
                          const roleIds = requirement.roleIds || (requirement.roleId ? [requirement.roleId] : ['']);
                          const primaryRole = roles.find(r => r.id === roleIds[0]);

                          return (
                            <div
                              key={index}
                              className="p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-2"
                              style={{ borderLeft: `4px solid ${primaryRole?.color || '#E5E7EB'}` }}
                            >
                              {/* Role slots — vertical stack with OR dividers */}
                              <div className="space-y-1.5">
                                {roleIds.map((rid, rIdx) => (
                                  <div key={rIdx} className="space-y-1">
                                    {rIdx > 0 && (
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 border-t border-gray-200" />
                                        <span className="text-[10px] font-semibold text-blue-500 px-1">OR</span>
                                        <div className="flex-1 border-t border-gray-200" />
                                      </div>
                                    )}
                                    <div className="flex items-center gap-1.5">
                                      <RoleSelector
                                        roles={roles}
                                        value={rid}
                                        onChange={(roleId) => {
                                          const updated = [...teamRequirements];
                                          const newIds = [...roleIds];
                                          newIds[rIdx] = roleId;
                                          updated[index] = { ...requirement, roleIds: newIds };
                                          setTeamRequirements(updated);
                                        }}
                                        placeholder="Select role..."
                                        className="flex-1"
                                      />
                                      {roleIds.length > 1 && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const updated = [...teamRequirements];
                                            updated[index] = { ...requirement, roleIds: roleIds.filter((_, i) => i !== rIdx) };
                                            setTeamRequirements(updated);
                                          }}
                                          className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = [...teamRequirements];
                                    updated[index] = { ...requirement, roleIds: [...roleIds, ''] };
                                    setTeamRequirements(updated);
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors w-full text-center"
                                >
                                  + Add OR alternative
                                </button>
                              </div>

                              {/* Count + delete */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500">Qty:</span>
                                  <div className="flex items-center gap-1 bg-white rounded border">
                                    <button type="button" onClick={() => {
                                      const updated = [...teamRequirements];
                                      updated[index].quantity = Math.max(1, (requirement.quantity || 1) - 1);
                                      setTeamRequirements(updated);
                                    }} className="w-6 h-6 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded text-xs">−</button>
                                    <span className="w-5 text-center text-sm font-medium">{requirement.quantity || 1}</span>
                                    <button type="button" onClick={() => {
                                      const updated = [...teamRequirements];
                                      updated[index].quantity = Math.min(10, (requirement.quantity || 1) + 1);
                                      setTeamRequirements(updated);
                                    }} className="w-6 h-6 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded text-xs">+</button>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setTeamRequirements(teamRequirements.filter((_, i) => i !== index))}
                                  className="text-gray-400 hover:text-red-500 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}

                        <button
                          type="button"
                          onClick={() => setTeamRequirements([...teamRequirements, { roleIds: [''], quantity: 1 }])}
                          className="w-full py-2 text-sm font-medium text-gray-600 border border-dashed border-gray-300 rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add Another Role
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </TabsContent>
              
              {/* Tab 3: Asset Photos */}
              <TabsContent value="photos" className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <ImageIcon className="w-4 h-4" />
                    Asset Photos ({assetImages.length}/5)
                  </Label>
                  
                  {assetImages.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                      {assetImages.map((url, index) => (
                        <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                          <img 
                            src={url} 
                            alt={`Asset ${index + 1}`} 
                            className="w-full h-full object-cover cursor-pointer"
                            onClick={() => {
                              setViewerImages(assetImages);
                              setViewerInitialIndex(index);
                              setViewerTitle(`${name || 'Asset'} Photos`);
                              setImageViewerOpen(true);
                            }}
                          />
                          {/* View button */}
                          <button
                            onClick={() => {
                              setViewerImages(assetImages);
                              setViewerInitialIndex(index);
                              setViewerTitle(`${name || 'Asset'} Photos`);
                              setImageViewerOpen(true);
                            }}
                            className="absolute bottom-1 left-1 p-1.5 bg-black/70 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                            title="View fullscreen"
                          >
                            <Eye className="w-3 h-3" />
                          </button>
                          {/* Remove button */}
                          <button
                            onClick={() => handleRemovePhoto(index)}
                            className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {assetImages.length < 5 && (
                    <div>
                      <input
                        type="file"
                        id="asset-photos"
                        accept="image/*"
                        multiple
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                      <SecondaryButton
                        className="w-full"
                        disabled={uploadingPhoto}
                        onClick={() => document.getElementById('asset-photos').click()}
                      >
                        {uploadingPhoto ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Photos ({5 - assetImages.length} remaining)
                          </>
                        )}
                      </SecondaryButton>
                      <p className="text-xs text-gray-500 mt-1">
                        Accepted: JPG, PNG, WEBP • Max 5 photos • Tap to view fullscreen
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              {/* Tab 4: Documents */}
              <TabsContent value="documents" className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4" />
                    Document Photos ({assetDocuments.length}/10)
                  </Label>
                  <p className="text-xs text-gray-600 mb-3">Photos of insurance, registration, certificates • Tap to view fullscreen</p>
                  
                  {assetDocuments.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                      {assetDocuments.map((doc, index) => (
                        <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                          <img 
                            src={doc.url} 
                            alt={doc.filename}
                            className="w-full h-full object-cover cursor-pointer"
                            onClick={() => {
                              setViewerImages(assetDocuments);
                              setViewerInitialIndex(index);
                              setViewerTitle(`${name || 'Asset'} Documents`);
                              setImageViewerOpen(true);
                            }}
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs p-1.5 truncate">
                            {doc.filename}
                          </div>
                          {/* View button */}
                          <button
                            onClick={() => {
                              setViewerImages(assetDocuments);
                              setViewerInitialIndex(index);
                              setViewerTitle(`${name || 'Asset'} Documents`);
                              setImageViewerOpen(true);
                            }}
                            className="absolute bottom-8 left-1 p-1.5 bg-black/70 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                            title="View fullscreen"
                          >
                            <Eye className="w-3 h-3" />
                          </button>
                          {/* Remove button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveDocument(index);
                            }}
                            className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {assetDocuments.length < 10 && (
                    <div>
                      <input
                        type="file"
                        id="asset-documents"
                        accept="image/*"
                        multiple
                        onChange={handleDocumentUpload}
                        className="hidden"
                      />
                      <SecondaryButton
                        className="w-full"
                        disabled={uploadingDocument}
                        onClick={() => document.getElementById('asset-documents').click()}
                      >
                        {uploadingDocument ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Document Photos ({10 - assetDocuments.length} remaining)
                          </>
                        )}
                      </SecondaryButton>
                      <p className="text-xs text-gray-500 mt-1">
                        Take photos of insurance, registration, certificates
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </StandardModal>
        )}
        
        {/* Mass Action Toolbar */}
        {massActionMode && (
          <MassActionToolbar
            selectedCount={selectedAssets.length}
            onSelectAll={selectAllAssets}
            onDeselectAll={deselectAllAssets}
            onDelete={() => setBulkDeleteConfirm(true)}
            itemName="asset"
          />
        )}

        {/* Content */}
        {!assets || assets.length === 0 ? (
          <EmptyState
            icon={Settings}
            title="No Assets Added Yet"
            description="Click 'Add' to register your first asset"
            actionLabel="Add New Asset"
            onAction={() => setShowAddModal(true)}
          />
        ) : viewMode === "card" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedAssets.map((asset) => {
              const isSelected = selectedAssets.includes(asset.id);
              const borderColors = getAssetBorderColors(asset);
              
              let iconColor = asset.active ? '#10B981' : '#6B7280';
              if (asset.teamRequirements && asset.teamRequirements.length > 0) {
                const req0 = asset.teamRequirements[0];
                const primaryId = (req0.roleIds && req0.roleIds[0]) || req0.roleId;
                const firstRole = roles.find(r => r.id === primaryId);
                if (firstRole) iconColor = firstRole.color;
              }
              
              const expandedContent = (
                <>
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      Team Required
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {asset.teamRequirements && asset.teamRequirements.length > 0 ? (
                        asset.teamRequirements.map((requirement, index) => (
                          <Badge 
                            key={index}
                            className="text-xs text-white"
                            style={{ 
                              backgroundColor: roles.find(r => r.id === requirement.roleId)?.color || '#3B82F6'
                            }}
                          >
                            <Check className="w-3 h-3 mr-1" />
                            {requirement.quantity} {requirement.roleName}
                          </Badge>
                        ))
                      ) : (
                        <Badge variant="outline" className="text-xs text-gray-500">
                          <Check className="w-3 h-3 mr-1" />
                          Self-service (no team required)
                        </Badge>
                      )}
                    </div>
                  </div>

                  {asset.notes && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-1">Notes</p>
                      <div className="p-2 bg-gray-50 rounded text-xs text-gray-600 italic">
                        {asset.notes}
                      </div>
                    </div>
                  )}
                  
                  {asset.photos && asset.photos.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                        <ImageIcon className="w-4 h-4" />
                        Photos ({asset.photos.length})
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {asset.photos.map((url, idx) => (
                          <img 
                            key={idx} 
                            src={url} 
                            alt={`${asset.name} ${idx + 1}`}
                            className="w-full aspect-square object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-75 transition"
                            onClick={() => {
                              setViewerImages(asset.photos);
                              setViewerInitialIndex(idx);
                              setViewerTitle(`${asset.name} Photos`);
                              setImageViewerOpen(true);
                            }}
                          />
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Tap to view fullscreen</p>
                    </div>
                  )}
                  
                  {asset.documents && asset.documents.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        Document Photos ({asset.documents.length})
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {asset.documents.map((doc, idx) => (
                          <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                            <img 
                              src={doc.url} 
                              alt={doc.filename}
                              className="w-full h-full object-cover cursor-pointer hover:opacity-75 transition"
                              onClick={() => {
                                setViewerImages(asset.documents);
                                setViewerInitialIndex(idx);
                                setViewerTitle(`${asset.name} Documents`);
                                setImageViewerOpen(true);
                              }}
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-1 truncate">
                              {doc.filename}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Tap to view fullscreen • Download available</p>
                    </div>
                  )}
                </>
              );
              
              return (
                <ResourceCard
                  key={asset.id}
                  id={asset.id}
                  name={asset.name}
                  icon={Package}
                  iconColor={iconColor}
                  description={undefined}
                  descriptionLines={[
                    { icon: <Users className="w-3 h-3" />, text: `Capacity: ${asset.capacity || 12}` },
                    { icon: <Shield className="w-3 h-3" />, text: asset.teamRequirements?.length
                        ? `${asset.teamRequirements.length} role${asset.teamRequirements.length > 1 ? 's' : ''} required`
                        : 'No team required'
                    },
                  ]}
                  showSeeMore={false}
                  leftBorderColors={borderColors}
                  massActionMode={massActionMode}
                  isSelected={isSelected}
                  onToggleSelect={() => toggleAssetSelection(asset.id)}
                  additionalClassNames={!asset.active ? 'opacity-60' : ''}
                  onEdit={() => handleEditClick(asset)}
                  onDelete={() => handleDeleteClick(asset)}
                />
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[768px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {massActionMode && (
                      <th className="w-12 px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedAssets.length === paginatedAssets.length && paginatedAssets.length > 0}
                          onChange={(e) => e.target.checked ? selectAllAssets() : deselectAllAssets()}
                          className="w-4 h-4 cursor-pointer rounded border-gray-300"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button onClick={() => handleSort("name")} className="flex items-center hover:text-gray-700">
                        Name {getSortIcon("name")}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        Team Requirements
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedAssets.map((asset) => {
                    const isSelected = selectedAssets.includes(asset.id);
                    return (
                      <tr key={asset.id} className={`${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'} transition-colors`}>
                        {massActionMode && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleAssetSelection(asset.id)}
                              className="w-4 h-4 cursor-pointer rounded border-gray-300"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-100"
                            >
                              <Package className="w-5 h-5 text-blue-600" />
                            </div>
                            <span className="font-medium text-gray-900">{asset.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {asset.teamRequirements && asset.teamRequirements.length > 0 ? (
                              asset.teamRequirements.map((requirement, index) => (
                                <Badge 
                                  key={index}
                                  className="text-xs text-white"
                                  style={{ 
                                    backgroundColor: roles.find(r => r.id === requirement.roleId)?.color || '#3B82F6'
                                  }}
                                >
                                  {requirement.quantity} {requirement.roleName}
                                </Badge>
                              ))
                            ) : (
                              <Badge variant="outline" className="text-xs text-gray-500">
                                <Check className="w-3 h-3 mr-1" />
                                No team required
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditClick(asset)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(asset)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {sortedAssets.length > itemsPerPage && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={sortedAssets.length}
          />
        )}

        {/* Delete Asset Modal */}
        <StandardModal
          isOpen={deleteConfirm.open}
          onClose={() => setDeleteConfirm({ open: false, id: null, name: "" })}
          title="Delete Asset?"
          description={`Are you sure you want to delete ${deleteConfirm.name}?`}
          size="sm"
          footer={
            <>
              <SecondaryButton
                onClick={() => setDeleteConfirm({ open: false, id: null, name: "" })}
                className="flex-1"
                disabled={isDeleting}
              >
                Cancel
              </SecondaryButton>
              <DangerButton
                onClick={handleDeleteConfirm}
                className="flex-1"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Asset'
                )}
              </DangerButton>
            </>
          }
        >
          <InfoCard variant="danger" showIcon={true}>
            <p className="font-medium mb-1">This action cannot be undone.</p>
            <p>This asset will be unassigned from all future trips.</p>
          </InfoCard>
        </StandardModal>
        
        {/* Bulk Delete Modal */}
        <StandardModal
          isOpen={bulkDeleteConfirm}
          onClose={() => setBulkDeleteConfirm(false)}
          title="Delete Multiple Assets?"
          description={`Are you sure you want to delete ${selectedAssets.length} asset(s)?`}
          size="sm"
          footer={
            <>
              <SecondaryButton
                onClick={() => setBulkDeleteConfirm(false)}
                className="flex-1"
                disabled={isBulkDeleting}
              >
                Cancel
              </SecondaryButton>
              <DangerButton
                onClick={handleBulkDelete}
                className="flex-1"
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  `Delete ${selectedAssets.length} Assets`
                )}
              </DangerButton>
            </>
          }
        >
          <InfoCard variant="danger" showIcon={true}>
            <p className="font-medium mb-1">This action cannot be undone.</p>
            <p>Assets assigned to trips may fail to delete.</p>
          </InfoCard>
        </StandardModal>
        
        {/* Fullscreen Image Viewer */}
        <ImageViewer
          isOpen={imageViewerOpen}
          onClose={() => setImageViewerOpen(false)}
          images={viewerImages}
          initialIndex={viewerInitialIndex}
          title={viewerTitle}
        />
      </div>
    </TabPageLayout>
  );
};

export default AssetsTab;
